import {
  Box,
  Breadcrumbs,
  Button,
  CircularProgress,
  Divider,
  dividerClasses,
  Grid,
  IconButton,
  ListItemText,
  Skeleton,
  Typography,
} from '@mui/material';
import React from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import AppsHeader from '@tenant/core/AppsContainer/AppsHeader';
import AppTooltip from '@tenant/core/AppTooltip';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import IntlMessages from '@tenant/utility/IntlMessages';
import useGoBack from '@tenant/utility/hooks/useGoBack';
import { ROUTE_STRING } from 'constants/RouteString';
import DockerAppTypes from 'constants/DockerAppTypes';
import DockerImageVersion from './components/Detail/DockerImageVersion';
import DeleteIcon from '@mui/icons-material/Delete';
import DeleteDockerImageAction from './components/Common/DeleteDockerImageAction';
import { Code } from 'react-content-loader';
import Error404 from 'pages/errorPages/Error404';
import RefreshIcon from '@mui/icons-material/Refresh';
import AccessTimeFilledIcon from '@mui/icons-material/AccessTimeFilled';
import CancelIcon from '@mui/icons-material/Cancel';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import useGetLastRunRepository from 'redux/slices/dockerImage/hooks/useGetLastRunRepository';
import LastRun from './components/Detail/LastRun';
import AppsContainer from '@tenant/core/AppsContainer';
import { AuthorizePermission } from '@tenant/utility/constants/authorizePermission';
import AllowedAccess from '@tenant/core/AppPermission/AllowedAccess';

const Detail = () => {
  const navigate = useNavigate();
  const goBack = useGoBack();

  const { repository } = useParams();
  const { data, isLoading, isRefetching, isError, refetch } =
    useGetLastRunRepository({ repository });
  const item = data?.data || {};
  const lastRun = item.LastRun ?? {};
  const loading = isLoading || isRefetching;

  const onClickBackButton = () => {
    goBack(ROUTE_STRING.DOCKER_IMAGE.LIST);
  };

  // Deliberately NOT `goBack`: after a delete the previous history entry can be a page
  // about the repository that no longer exists (or the add form that created it).
  const goToList = () => {
    navigate(ROUTE_STRING.DOCKER_IMAGE.LIST);
  };

  const goToUpgrade = () => {
    navigate(
      ROUTE_STRING.DOCKER_IMAGE.UPGRADE_VERSION.replace(
        ':repository',
        repository,
      ),
    );
  };

  if (isError) {
    return <Error404 />;
  }

  return (
    <AppsContainer
      fullView
      title={
        <Breadcrumbs aria-label='breadcrumb' sx={{ mb: 2 }}>
          <Typography color='text.primary'>Library</Typography>
          <Typography color='text.primary'>
            <Link to={ROUTE_STRING.DOCKER_IMAGE.LIST}>Docker Repositories</Link>
          </Typography>
          <Typography color='text.primary'>{data?.data?.Name}</Typography>
        </Breadcrumbs>
      }
    >
      <AppsHeader>
        <Box
          display={'flex'}
          justifyContent={'space-between'}
          sx={{ width: '100%' }}
        >
          <Box
            sx={{
              display: 'flex',
              cursor: 'pointer',
              alignItems: 'center',
              gap: 4,
            }}
            mr={{ xs: 2, sm: 4 }}
          >
            <AppTooltip title={<IntlMessages id='common.back' />}>
              <ArrowBackIcon
                sx={{
                  color: 'text.secondary',
                }}
                onClick={onClickBackButton}
              />
            </AppTooltip>
            {loading ? (
              <Skeleton width={150} height={75} />
            ) : (
              <Box
                display={'flex'}
                alignItems={'center'}
                sx={{
                  cursor: 'auto',
                  [`& .${dividerClasses.root}`]: {
                    height: 20,
                  },
                }}
              >
                <Box title={'Last run: ' + lastRun?.Status}>
                  {lastRun?.Status === 'Queued' && (
                    <AccessTimeFilledIcon color='primary' />
                  )}

                  {lastRun?.Status === 'Failed' && <CancelIcon color='error' />}

                  {lastRun?.Status === 'Succeeded' && (
                    <CheckCircleIcon color='success' />
                  )}

                  {lastRun?.Status === 'Running' && (
                    <CircularProgress size={20} />
                  )}
                </Box>

                <ListItemText
                  sx={{ ml: 4 }}
                  primary={
                    <>
                      <Typography variant='h4'>
                        {item.Name}{' '}
                        <IconButton
                          title='Refresh'
                          size='small'
                          onClick={refetch}
                        >
                          <RefreshIcon fontSize='inherit' />
                        </IconButton>
                      </Typography>{' '}
                    </>
                  }
                  secondary={
                    DockerAppTypes.find(
                      (e) => e.Value === lastRun.DeploymentType,
                    )?.Description || '-'
                  }
                  primaryTypographyProps={{
                    component: 'div',
                  }}
                />
              </Box>
            )}
          </Box>

          {loading ? (
            <Skeleton width={100} />
          ) : (
            <Box
              display={'flex'}
              alignItems={'center'}
              gap={2}
              sx={{
                [`& .${dividerClasses.root}`]: {
                  height: 20,
                },
              }}
            >
              <AllowedAccess
                permissionIds={[AuthorizePermission.REPOSITORY_DEPLOY]}
              >
                <Button
                  variant='outlined'
                  title='Deployment New Version'
                  onClick={goToUpgrade}
                >
                  Deployment New Version
                </Button>
              </AllowedAccess>

              <AllowedAccess
                permissionIds={[AuthorizePermission.REPOSITORY_DELETE]}
              >
                <DeleteDockerImageAction
                  selected={item}
                  trigger={
                    <IconButton variant='outlined' color='error' title='Delete'>
                      <DeleteIcon />
                    </IconButton>
                  }
                  handleSubmitDelete={goToList}
                />
              </AllowedAccess>
            </Box>
          )}
        </Box>
      </AppsHeader>

      <Box p={4}>
        {loading && <Code />}

        {!loading && (
          <Grid container spacing={5}>
            <Grid item xs={12}>
              {!!repository && <LastRun lastRun={lastRun} app={item?.App} />}
            </Grid>
            <Grid item xs={12}>
              <Typography variant='h4' mt={5} mb={5}>
                Image Versions
              </Typography>
              <DockerImageVersion repository={repository} />
            </Grid>
          </Grid>
        )}
      </Box>
    </AppsContainer>
  );
};

export default React.memo(Detail);
